import React, { useCallback, useEffect, useState } from 'react';
import {
  Battery,
  HardDrive,
  Monitor,
  Terminal,
  Trash2,
  Volume2,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { WifiFlyout } from './WifiFlyout';
import { useSimulation } from '../context/SimulationContext';
import { inferDesktopApp, windowTitle } from '../utils/inferDesktopApp';

function useNow(clockMs: number): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), clockMs);
    return () => window.clearInterval(id);
  }, [clockMs]);
  return now;
}

export function Taskbar({
  minimizedMap,
  onRestore,
}: {
  minimizedMap: Record<string, boolean>;
  onRestore: (id: string) => void;
}): React.ReactElement {
  const { state, bringToFront, logAction } = useSimulation();
  const { openWindows, activeWindow, isFixed } = state;
  const [flyOpen, setFlyOpen] = useState(false);

  useEffect(() => {
    function onDocMouseDown(ev: MouseEvent) {
      const el = ev.target as HTMLElement;
      if (el.closest('[data-wifi-flyout-root]')) return;
      if (el.closest('[data-taskbar-wifi]')) return;
      setFlyOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const now = useNow(1000);
  const timeStr = now.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
  const dateStr = now.toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  const toggleWifi = useCallback(() => {
    setFlyOpen((o) => {
      const next = !o;
      if (next) logAction('opened_wifi_flyout');
      return next;
    });
  }, [logAction]);

  return (
    <div className="relative shrink-0 z-[4000]">
      {flyOpen && (
        <div data-wifi-flyout-root className="contents">
          <WifiFlyout onClose={() => setFlyOpen(false)} />
        </div>
      )}

      <footer
        data-tutorial="taskbar"
        className="h-[52px] border-t border-white/10 bg-white/10 backdrop-blur-md flex items-stretch px-2 gap-2 shadow-[0_-8px_32px_rgba(0,0,0,0.35)]"
      >
        <div className="flex flex-1 items-center gap-1 min-w-0 overflow-x-auto py-1">
          {openWindows.map((wid) => {
            const kind = inferDesktopApp(wid);
            if (!kind) return null;
            const active = activeWindow === wid && !minimizedMap[wid];
            const TrayIcon =
              kind === 'command-prompt'
                ? Terminal
                : kind === 'network-settings'
                  ? Monitor
                  : kind === 'this-pc'
                    ? HardDrive
                    : Trash2;
            const label = windowTitle(kind);

            return (
              <button
                key={wid}
                type="button"
                title={label}
                aria-label={label}
                onClick={() => {
                  if (minimizedMap[wid]) {
                    onRestore(wid);
                  }
                  bringToFront(wid);
                }}
                className={`group flex items-center gap-2 h-10 min-w-[48px] max-w-[140px] px-2 rounded-md border transition-colors shrink-0 ${
                  active
                    ? 'bg-white/20 border-white/25'
                    : 'bg-transparent border-transparent hover:bg-white/10'
                }`}
              >
                <TrayIcon className="h-5 w-5 shrink-0 text-white/90" aria-hidden />
                <span className="hidden sm:inline truncate text-xs text-white/85">
                  {label}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-center px-2">
          <button
            type="button"
            aria-label="Start"
            className="grid h-10 w-10 place-items-center rounded-md hover:bg-white/10 transition-colors text-xl text-white shadow-inner shadow-black/20"
          >
            ⊞
          </button>
        </div>

        <div className="flex flex-1 items-center justify-end gap-2 sm:gap-4 pr-1 sm:pr-3">
          <div className="hidden sm:flex items-center gap-2 text-white/80">
            <Volume2 className="h-[18px] w-[18px]" aria-hidden />
            <Battery className="h-[18px] w-[18px]" aria-hidden />
          </div>

          <button
            type="button"
            data-taskbar-wifi
            data-tutorial="tray-wifi"
            aria-label={isFixed ? 'Wi-Fi connected' : 'Wi-Fi, no internet'}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              toggleWifi();
            }}
            className="relative flex h-9 w-10 items-center justify-center rounded-md hover:bg-white/10 transition-colors"
          >
            {isFixed ? (
              <Wifi className="h-5 w-5 text-emerald-400" strokeWidth={2} aria-hidden />
            ) : (
              <span className="relative flex items-center justify-center text-red-500">
                <span
                  className="absolute -top-0.5 -right-0.5 text-[10px] leading-none"
                  aria-hidden
                >
                  🔴
                </span>
                <WifiOff className="h-5 w-5" strokeWidth={2} aria-hidden />
              </span>
            )}
          </button>

          <button
            type="button"
            className="text-right leading-tight px-1 rounded hover:bg-white/10 transition-colors cursor-default"
            aria-hidden
          >
            <div className="text-xs font-medium text-white/90 tabular-nums">
              {timeStr}
            </div>
            <div className="text-[10px] text-white/55 hidden sm:block">
              {dateStr}
            </div>
          </button>
        </div>
      </footer>
    </div>
  );
}
